<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import { IUser } from '@/interfaces/IUser';
  import { IMember } from '@/interfaces/IMember';
  import UsersService from '@/api/services/users.service';
  import RestaurantsService from '@/api/services/restaurants.service';
  import { StoreSetupService } from '@/api/services/store-setup.service';
  import type { IStoreSetupResume } from '@/interfaces/IStoreSetup';
  import V2SetupResumeCard from '@/components/v2/store-setup/shell/V2SetupResumeCard.vue';
  import {
    dismissSetupResume,
    isSetupResumeDismissed,
  } from '@/composition/store-setup/setup-resume-dismissal';
  import V2DashboardWelcomeHero from '@/components/v2/dashboard/V2DashboardWelcomeHero.vue';
  import V2DashboardRestaurantCard from '@/components/v2/dashboard/V2DashboardRestaurantCard.vue';
  import V2DashboardQuickStats from '@/components/v2/dashboard/V2DashboardQuickStats.vue';
  import V2DashboardResourcesSection from '@/components/v2/dashboard/V2DashboardResourcesSection.vue';
  import V2DashboardCreateRestaurant from '@/components/v2/dashboard/V2DashboardCreateRestaurant.vue';
  import V2DashboardAnnouncements from '@/components/v2/dashboard/V2DashboardAnnouncements.vue';
  import V2DashboardTips from '@/components/v2/dashboard/V2DashboardTips.vue';
  import V2DashboardTutorial from '@/components/v2/dashboard/V2DashboardTutorial.vue';
  import V2DashboardPosAuth from '@/components/v2/dashboard/V2DashboardPosAuth.vue';
  import { useUi } from '@/composition/use-ui';

  // Props
  const props = defineProps<{
    user: IUser;
  }>();

  // Services
  const usersService = new UsersService();
  const restaurantsService = new RestaurantsService();
  const router = useRouter();
  const ui = useUi();

  // State
  const roles = ref<IMember[]>([]);
  const loading = ref(true);
  const statsLoading = ref(true);
  const totalViews = ref(0);
  const totalOrders = ref(0);
  const activeRestaurants = ref(0);
  /** An unfinished store of the setup flow (plan §4.12); hidden once declined in this visit. */
  const resume = ref<IStoreSetupResume | null>(null);

  // Computed
  const hasRestaurants = computed(() => roles.value.length > 0);

  const sortedRestaurants = computed(() => {
    return [...roles.value].sort((a, b) => {
      // Sort by restaurant creation date (DESC)
      const dateA = new Date(a.restaurant.createdAt);
      const dateB = new Date(b.restaurant.createdAt);
      const msA = dateA.getTime();
      const msB = dateB.getTime();
      return (
        (Number.isFinite(msB) ? msB : 0) - (Number.isFinite(msA) ? msA : 0)
      );
    });
  });

  // Methods
  const loadRestaurants = async () => {
    try {
      loading.value = true;
      const userRoles = await usersService.getRoles(props.user.id);
      roles.value = userRoles;

      // Count active restaurants (those with items)
      activeRestaurants.value = userRoles.map((role) => role.restaurant).length;
    } catch (error) {
      console.error('Error loading restaurants:', error);
      ui.serverError(error);
    } finally {
      loading.value = false;
    }
  };

  const loadAggregateStats = async () => {
    try {
      statsLoading.value = true;
      // TODO: Implement aggregate stats API endpoint
      // For now, we'll use placeholder values
      totalViews.value = 0;
      totalOrders.value = 0;
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      statsLoading.value = false;
    }
  };

  const loadResume = async () => {
    try {
      const found = await StoreSetupService.resume();
      resume.value =
        found && !isSetupResumeDismissed(found.restaurantId) ? found : null;
    } catch (error) {
      // An optional card: the dashboard works without it.
      console.error('Error loading the unfinished store:', error);
    }
  };

  // The creation flow lives at /new (it shows the partner notice itself).
  const createNewRestaurant = () => {
    void router.push('/new');
  };

  const continueResume = () => {
    if (resume.value) void router.push(`/${resume.value.stringId}/setup`);
  };

  // «Crear una tienda nueva»: /new must not offer the same store again.
  const dismissResume = () => {
    if (resume.value) dismissSetupResume(resume.value.restaurantId);
    resume.value = null;
    createNewRestaurant();
  };

  // Lifecycle
  onMounted(async () => {
    // Signups of old bundles and old links still land on /dashboard?new.
    if (router.currentRoute.query.new !== undefined) {
      void router.replace('/new');
      return;
    }
    await Promise.all([loadRestaurants(), loadAggregateStats(), loadResume()]);
  });
</script>

<template>
  <div class="v2-dashboard-container">
    <!-- First on every screen size: the phone is where the flow sends owners back -->
    <V2SetupResumeCard
      v-if="resume"
      class="resume-card"
      :resume="resume"
      @continue="continueResume"
      @dismiss="dismissResume"
    />

    <!-- Welcome Hero Section -->
    <V2DashboardWelcomeHero
      :user="user"
      :restaurant-count="roles.length"
      :active-restaurants="activeRestaurants"
    />

    <!-- Quick Stats Section -->
    <!-- <V2DashboardQuickStats
      v-if="hasRestaurants"
      :total-views="totalViews"
      :total-orders="totalOrders"
      :restaurant-count="roles.length"
      :loading="statsLoading"
    /> -->

    <!-- Mobile Announcements & Tips (shown above restaurants on mobile) -->
    <div class="mobile-highlights-section">
      <V2DashboardPosAuth />
      <V2DashboardAnnouncements />
      <V2DashboardTips />
    </div>

    <!-- Main Content -->
    <div class="dashboard-main-content">
      <!-- Restaurants Section -->
      <div class="restaurants-section">
        <div class="section-header">
          <h2 class="section-title">
            <v-icon>mdi-store</v-icon>
            {{ $t('dashboard.myStores') }}
          </h2>
          <!-- The resume card offers «Crear una tienda nueva» itself: one orange action -->
          <v-btn
            v-if="hasRestaurants && !resume"
            color="primary"
            @click="createNewRestaurant"
            class="create-btn"
          >
            <v-icon left>mdi-plus</v-icon>
            Crear nueva tienda
          </v-btn>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="restaurants-grid">
          <v-skeleton-loader
            v-for="i in 3"
            :key="i"
            type="card"
            class="restaurant-skeleton"
          />
        </div>

        <!-- Restaurant Cards -->
        <div v-else-if="hasRestaurants" class="restaurants-grid">
          <V2DashboardRestaurantCard
            v-for="role in sortedRestaurants"
            :key="role.restaurantId"
            :role="role"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <V2DashboardCreateRestaurant @create="createNewRestaurant" />
        </div>
      </div>

      <!-- Resources Section -->
      <div class="resources-section">
        <V2DashboardResourcesSection />
      </div>
    </div>

    <!-- Mobile Tutorial Section (shown at bottom on mobile) -->
    <div class="mobile-tutorial-section">
      <V2DashboardTutorial />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-dashboard-container {
    @apply tw-min-h-screen tw-max-w-screen-2xl tw-mx-auto;
    @apply tw-pb-8;
    @apply tw-pt-6 md:tw-pt-12; // Add top padding for sticky header
    @apply tw-px-4 md:tw-px-6 xl:tw-px-12; // Add horizontal padding

    .resume-card {
      @apply tw-mb-6;
    }

    .mobile-highlights-section {
      @apply tw-block lg:tw-hidden;
      @apply tw-mb-6 tw-space-y-4;
    }

    .mobile-tutorial-section {
      @apply tw-block lg:tw-hidden;
      @apply tw-mt-6;
    }

    .dashboard-main-content {
      @apply tw-mt-6;
      @apply tw-grid tw-gap-6;
      @apply tw-grid-cols-1 lg:tw-grid-cols-3;

      .restaurants-section {
        @apply lg:tw-col-span-2;

        .section-header {
          @apply tw-flex tw-items-center tw-justify-between;
          @apply tw-mb-4;

          .section-title {
            @apply tw-text-2xl tw-font-light tw-text-gray-800;
            @apply tw-flex tw-items-center tw-gap-2;
          }

          .create-btn {
            @apply tw-font-light;
          }
        }

        .restaurants-grid {
          @apply tw-grid tw-gap-4;
          @apply sm:tw-grid-cols-2;
          @apply 2xl:tw-grid-cols-3;

          .restaurant-skeleton {
            @apply tw-h-64;
          }
        }

        .empty-state {
          @apply tw-flex tw-items-center tw-justify-center;
          @apply tw-min-h-[400px];
        }
      }

      .resources-section {
        @apply lg:tw-col-span-1;
        @apply tw-hidden lg:tw-block;
      }
    }
  }

  // Mobile adjustments
  @media (max-width: 640px) {
    .v2-dashboard-container {
      @apply tw-pb-4;
      @apply tw-px-2; // Override horizontal padding on mobile

      .dashboard-main-content {
        @apply tw-mt-4;

        .restaurants-section {
          .section-header {
            @apply tw-flex-col tw-items-start tw-gap-2;

            .create-btn {
              @apply tw-w-full;
            }
          }
        }
      }
    }
  }
</style>
